/**
 * EventBookingPage - Unified booking page for Events & Functions
 * Routes to VenueBooking, DJBooking, PhotographyBooking, Equipment, Planner based on service type
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Star, MapPin, Check, Loader2, Building, Music, Camera, Package, Palette, ClipboardList, Eye } from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { toast } from 'sonner';
import VenueBookingFlow from '../../components/events/VenueBookingFlow';
import DJBookingFlow from '../../components/events/DJBookingFlow';
import PhotographyBookingFlow from '../../components/events/PhotographyBookingFlow';
import DecoratorQuoteFlow from '../../components/events/DecoratorQuoteFlow';
import EquipmentRentalFlow from '../../components/events/EquipmentRentalFlow';
import EventPlannerBookingFlow from '../../components/events/EventPlannerBookingFlow';
import VenueTourBooking from '../../components/events/VenueTourBooking';
import ChatWidget from '../../components/chat/ChatWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventBookingPage() {
  const { providerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  
  const serviceType = searchParams.get('type') || provider?.service_type;
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';

  useEffect(() => {
    const fetchProvider = async () => {
      if (!providerId) return;
      try {
        const response = await fetch(`${API_URL}/api/events/providers/${providerId}`);
        if (response.ok) {
          const data = await response.json();
          setProvider(data);
          // Auto-open booking modal
          setBookingOpen(true);
        } else {
          toast.error('Provider not found');
          navigate('/events');
        }
      } catch (error) {
        console.error('Failed to fetch provider:', error);
        toast.error('Failed to load provider details');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [providerId, navigate]);

  const handleBookingComplete = () => {
    setBookingOpen(false);
    toast.success('Booking submitted successfully!');
    navigate('/events');
  };

  const getProviderIcon = () => {
    switch (provider?.service_type) {
      case 'event-venues': return Building;
      case 'djs-bands': return Music;
      case 'photography-videography': return Camera;
      case 'party-equipment-hire': return Package;
      case 'event-decoration': return Palette;
      case 'event-planning': return ClipboardList;
      default: return Building;
    }
  };

  // Determine booking flow type
  const getBookingFlowType = () => {
    switch (provider?.service_type) {
      case 'event-venues': return 'venue';
      case 'djs-bands': return 'dj';
      case 'photography-videography': return 'photography';
      case 'event-decoration': return 'decorator';
      case 'party-equipment-hire': return 'equipment';
      case 'event-planning': return 'planner';
      default: return 'venue';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <p className="text-gray-600 mb-4">Provider not found</p>
        <Button onClick={() => navigate('/events')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>
      </div>
    );
  }

  const Icon = getProviderIcon();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 px-4">
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate('/events')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Events
          </button>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{provider.provider_name}</h1>
              <p className="text-purple-200 capitalize">{provider.service_type?.replace(/-/g, ' ')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Provider Info */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>About {provider.provider_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">{provider.description}</p>
              
              <div className="flex items-center gap-4 mb-4">
                <span className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
                  <span className="font-bold">{provider.rating?.toFixed(1) || 'New'}</span>
                  {provider.review_count > 0 && (
                    <span className="text-gray-500">({provider.review_count} reviews)</span>
                  )}
                </span>
                <span className="flex items-center gap-1 text-gray-500">
                  <MapPin className="w-4 h-4" />
                  {provider.city}
                </span>
              </div>

              {/* Event Types */}
              {provider.event_types?.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-700 mb-2">Specializes in</h4>
                  <div className="flex flex-wrap gap-2">
                    {provider.event_types.map(type => (
                      <Badge key={type} variant="outline" className="capitalize">
                        {type}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Verification */}
              {provider.verification_level >= 2 && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-800 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    Verified Provider
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Booking Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Book Now</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <span className="text-gray-500 text-sm">Starting from</span>
                <p className="text-2xl font-bold text-purple-600">
                  {currencySymbol}{provider.starting_price?.toLocaleString()}
                </p>
              </div>
              
              <Button 
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
                onClick={() => setBookingOpen(true)}
                data-testid="open-booking-btn"
              >
                {['CATERER', 'DECORATOR'].includes(provider.provider_type) 
                  ? 'Request Quote' 
                  : 'Book Now'}
              </Button>
              
              {/* Venue Tour Button */}
              {provider.service_type === 'event-venues' && (
                <Button 
                  variant="outline"
                  className="w-full mt-2"
                  onClick={() => setTourOpen(true)}
                  data-testid="schedule-tour-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Schedule a Tour
                </Button>
              )}
              
              <p className="text-xs text-gray-500 mt-3 text-center">
                {provider.service_type === 'event-venues' 
                  ? 'Booking request - venue will confirm within 24h'
                  : 'Instant booking available'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Packages/Services */}
        {provider.packages?.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Packages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {provider.packages.map((pkg, idx) => (
                  <div key={idx} className="p-4 border rounded-lg hover:border-purple-300 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold">{pkg.name}</h4>
                        <p className="text-sm text-gray-500">{pkg.duration}</p>
                      </div>
                      <span className="font-bold text-purple-600">
                        {currencySymbol}{pkg.price?.toLocaleString()}
                      </span>
                    </div>
                    {pkg.includes && (
                      <ul className="mt-2 text-sm text-gray-600">
                        {pkg.includes.map((item, i) => (
                          <li key={i} className="flex items-center gap-1">
                            <Check className="w-3 h-3 text-green-500" /> {item}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Booking Modals */}
      {provider.service_type === 'event-venues' && (
        <>
          <VenueBookingFlow
            open={bookingOpen}
            onClose={() => setBookingOpen(false)}
            venue={provider}
            onBookingComplete={handleBookingComplete}
          />
          <VenueTourBooking
            open={tourOpen}
            onClose={() => setTourOpen(false)}
            venue={provider}
            onTourBooked={() => {
              setTourOpen(false);
              toast.success('Tour scheduled!');
            }}
          />
        </>
      )}
      
      {provider.service_type === 'djs-bands' && (
        <DJBookingFlow
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          provider={provider}
          onBookingComplete={handleBookingComplete}
        />
      )}

      {provider.service_type === 'photography-videography' && (
        <PhotographyBookingFlow
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          provider={provider}
          onBookingComplete={handleBookingComplete}
        />
      )}

      {provider.service_type === 'event-decoration' && (
        <DecoratorQuoteFlow
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          provider={provider}
          onQuoteSubmitted={handleBookingComplete}
        />
      )}

      {provider.service_type === 'party-equipment-hire' && (
        <EquipmentRentalFlow
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          provider={provider}
          onBookingComplete={handleBookingComplete}
        />
      )}

      {provider.service_type === 'event-planning' && (
        <EventPlannerBookingFlow
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          provider={provider}
          onBookingComplete={handleBookingComplete}
        />
      )}

      {/* Chat Widget */}
      {provider && (
        <ChatWidget
          providerId={provider.id}
          providerName={provider.provider_name}
          customerName="Customer"
        />
      )}
    </div>
  );
}
